import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NewHireFormValues } from "features/PendingNewHire/Form/definition";
import { pendingNewHireKeys } from "features/PendingNewHire/hooks";
import { apiNewHireListPath, v2ApiNewHireJourneyPathById, v2ApiNewHireJourneySkipRolePathById } from "helpers/url/api/v2";
import { pick } from "lodash";
import { useCallback } from "react";
import apiClient from "services/ApiClient";
import { useToast } from "@chakra-ui/react";
import { setNewUserInQueryClient } from "features/User/hooks";
import { NEW_HIRE_JOURNEY_FIELDS, USER_FIELDS } from "definitions/constants/fields";
import { NewHireJourney } from "models/v2/newHireJourney";
import { createQueryString } from "helpers/queryString";
import newHireJourneyBatcher from "features/NewHireJourney/batcher";
import { taskKeys } from "features/Resources/Task/hooks";
import { meetingKeys } from "features/Resources/Meeting/hooks";
import { issueKeys } from "features/Issues/hooks";

export const newHireJourneyKeys = {
  all: ["newHireJourneys"] as const,
  lists: () => [...newHireJourneyKeys.all, "list"] as const,
  list: (filters: string) => [...newHireJourneyKeys.lists(), { filters }] as const,
  listByPathId: (pathId: string) => [...newHireJourneyKeys.lists(), "byPathId", pathId],
  withActiveIssuesList: () => [...newHireJourneyKeys.all, "withActiveIssuesList"] as const,
  details: () => [...newHireJourneyKeys.all, "detail"] as const,
  detail: (id: string) => [...newHireJourneyKeys.details(), id] as const,
  attentionStatuses: (ids: string[]) => [...newHireJourneyKeys.all, "attentionStatuses", [...ids].sort().join(",")] as const,
};

export type JourneyAttentionStatus = "needs_attention" | "tasks_overdue" | "tasks_open" | "all_clear";

export interface JourneyAttention {
  attentionStatus: JourneyAttentionStatus;
  overdueCount: number;
  openCount: number;
  issueCount: number;
  unscheduledMeetingCount: number;
}

export const useJourneyAttentionStatusesQuery = (ids: string[], options: QueryOptions = {}) => {
  const sortedIds = [...ids].sort();
  return useQuery<Record<string, JourneyAttention>>({
    queryKey: newHireJourneyKeys.attentionStatuses(sortedIds),
    queryFn: async () => {
      if (sortedIds.length === 0) return {};
      const query = sortedIds.map((id) => `ids[]=${encodeURIComponent(id)}`).join("&");
      return apiClient.get(`/api/new-hire-journeys/attention-statuses?${query}`);
    },
    enabled: options.enabled !== undefined ? options.enabled : sortedIds.length > 0,
    staleTime: 1000 * 60, // 1 minute — slightly fresher than detail since pill is the alert surface
  });
};

export function setNewNewHireJourneyInQueryClient(queryClient: QueryClient, newHire: NewHireJourney) {
  queryClient.setQueryData(newHireJourneyKeys.detail(newHire.id), (oldData: NewHireJourney | undefined) => {
    if (!oldData) {
      return newHire;
    }
    return {
      ...oldData,
      ...newHire,
    };
  });
}

interface QueryOptions {
  enabled?: boolean;
  [key: string]: any;
}

export const newHireJourneyQueryArgs = (id: string | undefined, options: QueryOptions = {}) => ({
  queryKey: id ? newHireJourneyKeys.detail(id) : [],
  queryFn: id ? async () => {
    const result = await newHireJourneyBatcher.fetch({ id });
    return result ?? undefined;
  } : async () => undefined,
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
  enabled: options?.enabled !== undefined ? options.enabled && !!id : !!id,
});

export const useNewHireJourneyDetailQuery = (id: string | undefined, options: QueryOptions = {}) => useQuery<NewHireJourney | undefined>({
  ...newHireJourneyQueryArgs(id, options),
});

export const usePreloadNewHireJourneyDetailQuery = (id: string | undefined, options: QueryOptions = {}) => {
  const queryClient = useQueryClient();
  return queryClient.prefetchQuery({ ...newHireJourneyQueryArgs(id), ...options });
};

export const useNewHiresListQuery = (queryString: string, options: QueryOptions = {}) => useQuery({
  queryKey: queryString ? newHireJourneyKeys.list(queryString) : newHireJourneyKeys.lists(),
  queryFn: async () => apiClient.get(apiNewHireListPath(queryString)),
  staleTime: 1000 * 60 * 3, // 3 minutes
  ...options,
});

export const useNewHireListByManagerIdQuery = (managerId: string, options: QueryOptions = {}) => {
  const queryString = createQueryString({
    additionalQueries: { managerId },
  });

  return useNewHiresListQuery(queryString, options);
};

export function useUpdateNewHireJourney(id: string, extractData = true) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (data: Partial<NewHireFormValues & { actionsStatus: "enabled" | "disabled" }>) => {
      let newHire = data?.newHire ?? {};
      let newHireJourney = data?.newHireJourney ?? {};
      let paths = data?.pathIds ? { pathIds: data.pathIds } : {};
      if (extractData) {
        newHire = pick(data, USER_FIELDS);
        newHireJourney = pick(data, NEW_HIRE_JOURNEY_FIELDS);
        paths = data?.pathIds ? { pathIds: data.pathIds } : {};
      }
      return apiClient.put<NewHireJourney>(v2ApiNewHireJourneyPathById(id), {
        ...(Object.keys(newHire).length ? { newHire } : {}),
        ...(Object.keys(newHireJourney).length ? { newHireJourney } : {}),
        ...(Object.keys(paths).length ? { paths } : {}),
      });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pendingNewHireKeys.count() });
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
      setNewUserInQueryClient(queryClient, res?.newHire);
      setNewNewHireJourneyInQueryClient(queryClient, res);
    },
    onError: (e) => {
      console.log(e);
      toast({
        title: "Error",
        description: "There was an error, please try again.",
        status: "error",
      });
    },
  });
}

export function useUpdateNewHireJourneyStatus(newHireJourneyId: string) {
  const toast = useToast();
  const mutation = useUpdateNewHireJourney(newHireJourneyId);
  const { mutate, ...rest } = mutation;

  const toggle = useCallback((actionsStatus: "enabled" | "disabled" | "completed") => {
    const newStatus = actionsStatus === "enabled" ? "disabled" : "enabled";
    mutate({ actionsStatus: newStatus }, {
      onSuccess: (res) => {
        toast({ title: `Automations ${res?.actionsStatus === "disabled" ? "paused" : "running"}` });
      },
    });
  }, [mutate, toast]);

  return { ...rest, toggle };
}

export function useSkipRoleForJourney(id: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (role: string) => apiClient.post<NewHireJourney>(
      v2ApiNewHireJourneySkipRolePathById(id),
      { role },
    ),
    onSuccess: (res) => {
      setNewNewHireJourneyInQueryClient(queryClient, res);
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(id) });
      // Skipped actions/issues changed — refresh this journey's sourced lists.
      queryClient.invalidateQueries({ queryKey: taskKeys.byNewHireJourney(id) });
      queryClient.invalidateQueries({ queryKey: meetingKeys.byNewHireJourney(id) });
      queryClient.invalidateQueries({ queryKey: issueKeys.byNewHireJourneyId(id) });
    },
    onError: () => {
      toast({
        title: "Couldn't skip",
        description: "Please try again.",
        status: "error",
      });
    },
  });
}

export function useCloseNewHireJourney(id: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ closeReason }: { closeReason?: string } = {}) => apiClient.post<NewHireJourney>(
      `/api/new-hire-journeys/${id}/close`,
      { closeReason },
    ),
    onSuccess: (res) => {
      setNewNewHireJourneyInQueryClient(queryClient, res);
      queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ["peopleTeamTaskGrid"] });
      toast({ title: "Journey closed", status: "success" });
    },
    onError: () => {
      toast({
        title: "Couldn't close journey",
        description: "Please try again.",
        status: "error",
      });
    },
  });
}
